import type { GridColDef } from '@mui/x-data-grid';
import type { GetMeasurementsStatsResponse } from '@vy/api-definition';
import { getSelectorAttribute, usagePageSelectors } from '@vy/app-selectors';
import type { ItemOf } from '@vy/utils';
import { formatNumber, purge, vyDate } from '@vy/utils';
import React from 'react';
import type { FieldPath } from 'react-hook-form';
import type { VyTableMinimalProps } from '../../../../components/new-design/table/vy-table/vy-table-types';
import { VyTable } from '../../../../components/new-design/table/vy-table/VyTable';
import { MeasurementDotMenu } from './MeasurementDotMenu/MeasurementDotMenu';
import { Link } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { sliceLongString } from '../../../../utils/string';

type DataMetringMeasurementsTableProps = VyTableMinimalProps<
  ItemOf<GetMeasurementsStatsResponse['items']>
> & {
  selectedMeasurementsIds: string[];
  checkboxSelectionAllowed: boolean;
  totalValue: number;
};

export const DataMetringMeasurementsTable: React.FC<DataMetringMeasurementsTableProps> = ({
  selectedMeasurementsIds,
  itemsCount,
  items,
  loadingItems = false,
  checkboxSelectionAllowed,
  totalValue,
  ...props
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
    <VyTable
      checkboxVisibleOnHover
      checkboxSelection={checkboxSelectionAllowed}
      itemsCount={itemsCount}
      items={items}
      columns={getColumns(checkboxSelectionAllowed)}
      loadingItems={loadingItems}
      sx={{ flex: 1, minHeight: 0 }}
      {...props}
    />
    <Stack
      direction="row"
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
      sx={{
        justifyContent: "space-between",
        position: 'absolute',
        left: 20,
        bottom: 16
      }}>
      {checkboxSelectionAllowed && (
        <span>{`Selected measurements: ${selectedMeasurementsIds?.length ?? 0}`}</span>
      )}
      <span>
        Total usage: <span className="font-bold">{formatNumber(totalValue)}</span> units
      </span>
    </Stack>
  </Box>
);

export const DATA_METERING_MEASUREMENTS_KEYS = Object.freeze({
  id: 'id',
  day: 'day',
  customerId: 'customerExternalId',
  customer: 'customer',
  product: 'product',
  subProduct: 'productVariant',
  quantity: 'quantity',
  price: 'price',
  dotMenu: 'dotMenu',
} satisfies Record<string, FieldPath<ItemOf<GetMeasurementsStatsResponse['items']>> | 'dotMenu'>);

function getColumns(showEditMenu: boolean): GridColDef<ItemOf<GetMeasurementsStatsResponse['items']>>[] {
  return purge([
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.day,
      headerName: 'Day',
      flex: 1,
      headerAlign: 'left',
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.day)}>
          {vyDate(value).format('DD MMM YYYY')}
        </span>
      ),
      sortable: false,
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.customerId,
      headerName: 'Customer ID',
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.customerId)}>{value}</span>
      ),
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.customer,
      headerName: 'Customer',
      flex: 1,
      sortable: false,
      renderCell: ({ row: { customerId }, value }) => (
        <Link to={`../../customers/${customerId}/overview`}>
          <Typography variant="body2" sx={{ '&:hover': { textDecoration: 'underline' } }}>
            {sliceLongString(value, 30)}
          </Typography>
        </Link>
      ),
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.product,
      headerName: 'Product',
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.product)}>{value}</span>
      ),
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.subProduct,
      headerName: 'Sub Product',
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.subProduct)}>{value}</span>
      ),
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.quantity,
      headerName: 'Quantity',
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.quantity)}>{value}</span>
      ),
    },
    {
      field: DATA_METERING_MEASUREMENTS_KEYS.price,
      headerName: 'Price',
      flex: 1,
      sortable: false,
      renderCell: ({ value }) => (
        <span {...getSelectorAttribute(usagePageSelectors.measurementsTable.price)}>{value}</span>
      ),
    },
    showEditMenu
      ? {
          field: DATA_METERING_MEASUREMENTS_KEYS.dotMenu,
          headerName: '',
          width: 50,
          sortable: false,
          renderCell: ({ row: { id, quantity } }) => (
            <MeasurementDotMenu currentUnits={quantity} measurementId={id} />
          ),
        }
      : undefined,
  ]);
}
